import { supabase } from "@/integrations/supabase/client";
import { ImpersonationService } from "@/services/users/ImpersonationService";
import { UserService } from "@/services/users/UserService";

export class AgentCacheManager {
  private static CACHE_KEY = "userAgents";
  private static ONE_DAY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  public static async getUserAgentsIds(): Promise<string[] | string> {
    // prettier-ignore
    const { data: { user }} = await UserService.getUser();
    if (!user) throw new Error("You must be logged in.");

    // When impersonating, always fetch fresh data and skip cache
    const impersonationState = ImpersonationService.getImpersonationState();

    if (impersonationState) {
      console.log(
        `Impersonation active. Admin User ID: ${impersonationState.adminUserId}, Impersonated User ID: ${impersonationState.impersonatedUserId}`
      );
    }

    // Get current user agent IDs with cache expiration
    let agentIds: string[] = [];
    const storedData = localStorage.getItem(this.CACHE_KEY);
    const now = Date.now();

    let shouldFetch = true;

    // Skip cache during impersonation - always fetch fresh data
    if (!impersonationState && storedData) {
      try {
        const { agents, timestamp } = JSON.parse(storedData);
        // Check if cache is less than 24 hours old
        if (timestamp && now - timestamp < this.ONE_DAY) {
          agentIds = agents.map((a: any) => a.id);
          shouldFetch = false;
        }
      } catch (e) {
        console.error("Error parsing cached userAgents:", e);
        localStorage.removeItem(this.CACHE_KEY);
      }
    }

    if (shouldFetch) {
      const { data: fetchedAgents, error: agentsError } = await supabase
        .from("agents")
        .select("id")
        .eq("user_id", user.id)
        .or("is_marketplace_genie.is.null,is_marketplace_genie.eq.false");

      if (agentsError) {
        console.error("Error fetching user agents:", agentsError);
        throw new Error("Failed to fetch user agents");
      }

      agentIds = (fetchedAgents || []).map((a) => a.id);

      // Don't cache during impersonation - always fetch fresh data
      if (!impersonationState) {
        // Only cache if user has agents, otherwise remove stale cache
        if (agentIds.length > 0) {
          localStorage.setItem(
            this.CACHE_KEY,
            JSON.stringify({
              agents: fetchedAgents || [],
              timestamp: now,
            })
          );
        } else {
          // User has no agents - clear any stale cache
          localStorage.removeItem(this.CACHE_KEY);
        }
      }
    }

    if (!agentIds.length) {
      return "No agents found for the current user";
    }
    return agentIds;
  }

  public static invalidateCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
  }
}
